import type { SetStateAction } from 'react';
import {
	type ActionSubjectAndAction,
	fireTrackAnalytics,
	type AnalyticsAttributes,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import {
	ADD_PRIORITY_SCHEME_MODAL_SOURCE_NAME,
	EDIT_PRIORITY_SCHEME_MODAL_SOURCE_NAME,
	addPrioritySchemeTrackEvent,
	editPrioritySchemeTrackEvent,
} from '../../common/constants';
import {
	PRIORITY_SCHEME_DESCRIPTION_FIELD,
	PRIORITY_SCHEME_NAME_FIELD,
} from '../../common/ui/constants';
import type {
	RequestMappings,
	CreateOrEditPriorityScheme,
	Priority,
	PriorityMappings,
	Project,
} from '../../common/ui/types';
import {
	createPrioritySchemeRequest,
	type CreatePrioritySchemeRequest,
} from '../../services/create-priority-scheme';
import {
	editPrioritySchemeRequest,
	type EditPrioritySchemeRequest,
} from '../../services/edit-priority-scheme';
import type { ApiValidationFailure } from '../../services/types';

export interface Fields {
	name: string;
	description: string;
	priorityIds: string;
	projectIds: string;
}

type Diff = {
	add: string[];
	remove: string[];
};

const getDefaultPriorityId = (priorities: Priority[]) =>
	priorities.find(({ isDefault }) => isDefault)?.id || '';

export const calculatePrioritiesDiff = (
	existingPriorities: Priority[],
	newPriorities: Priority[],
) => {
	const add: string[] = [];
	const remove: string[] = [];
	const existingPriorityIds: Record<string, true> = {};
	const newPriorityIds: Record<string, true> = {};

	newPriorities.forEach(({ id }) => {
		newPriorityIds[id] = true;
	});

	existingPriorities.forEach(({ id }) => {
		existingPriorityIds[id] = true;
	});

	newPriorities.forEach(({ id }) => {
		if (!existingPriorityIds[id]) {
			add.push(id);
		}
	});
	existingPriorities.forEach(({ id }) => {
		if (!newPriorityIds[id]) {
			remove.push(id);
		}
	});

	return { add, remove };
};

export const calculateProjectsDiff = (changes: Record<string, boolean>) => {
	const pendingProjectIds = Object.keys(changes);
	const add = pendingProjectIds.filter((projectId) => changes[projectId]);
	const remove = pendingProjectIds.filter((projectId) => !changes[projectId]);
	return {
		add,
		remove,
	};
};

export const generateAddPrioritySchemeParams = (
	priorityScheme: CreateOrEditPriorityScheme,
	projectsDiff: Diff,
): CreatePrioritySchemeRequest => ({
	name: priorityScheme.name,
	description: priorityScheme.description,
	priorityIds: priorityScheme.priorities.map(({ id }) => id),
	defaultPriorityId: getDefaultPriorityId(priorityScheme.priorities),
	projectIds: projectsDiff.add,
});

export const generateEditPrioritySchemeParams = (
	priorityScheme: CreateOrEditPriorityScheme,
	prioritiesDiff: Diff,
	projectsDiff: Diff,
): EditPrioritySchemeRequest => ({
	name: priorityScheme.name,
	description: priorityScheme.description,
	priorities: {
		add: { ids: prioritiesDiff.add },
		remove: { ids: prioritiesDiff.remove },
	},
	defaultPriorityId: getDefaultPriorityId(priorityScheme.priorities),
	projects: {
		add: { ids: projectsDiff.add },
		remove: { ids: projectsDiff.remove },
	},
});

export const savePrioritySchemeRequest = (
	priorityScheme: CreateOrEditPriorityScheme,
	prioritiesDiff: Diff,
	projectsDiff: Diff,
	mappings?: RequestMappings,
) => {
	let createParams = generateAddPrioritySchemeParams(priorityScheme, projectsDiff);
	let editParams = generateEditPrioritySchemeParams(priorityScheme, prioritiesDiff, projectsDiff);

	if (mappings) {
		createParams = {
			...createParams,
			mappings,
		};
		editParams = {
			...editParams,
			mappings,
		};
	}

	return priorityScheme.id
		? editPrioritySchemeRequest(priorityScheme.id, editParams)
		: createPrioritySchemeRequest(createParams);
};

export const handleApiValidationErrors = (
	response: ApiValidationFailure,
	setAPIValidationError: (
		value: SetStateAction<{
			name: string;
			description: string;
			priorityIds: string;
			projectIds: string;
		}>,
	) => void,
): void => {
	let hasKnownErrors = false;
	response.errors.forEach(({ field, message }) => {
		if (field === PRIORITY_SCHEME_NAME_FIELD) {
			setAPIValidationError((fields: Fields) => ({
				...fields,
				name: message,
			}));
			hasKnownErrors = true;
		} else if (field === PRIORITY_SCHEME_DESCRIPTION_FIELD) {
			setAPIValidationError((fields: Fields) => ({
				...fields,
				description: message,
			}));
			hasKnownErrors = true;
		}
	});

	if (!hasKnownErrors) {
		// there are only unknown validation errors that we can't properly show in the UI
		// so we are falling back to our "unknown error" flow
		throw new Error('unexpected error');
	}
};

export const generateScreenEventMeta = (priorityScheme: CreateOrEditPriorityScheme) => {
	const isCreateScheme = !priorityScheme.id;

	const SCREEN_ANALYTICS_EVENT_SOURCE_NAME = isCreateScheme
		? ADD_PRIORITY_SCHEME_MODAL_SOURCE_NAME
		: EDIT_PRIORITY_SCHEME_MODAL_SOURCE_NAME;

	const attributes = isCreateScheme
		? {}
		: {
				prioritySchemeId: priorityScheme.id,
				priorities: priorityScheme.priorities.map(({ id }) => id),
				prioritySchemePriorityCount: priorityScheme.priorities.length,
				prioritySchemeHasDescription: Boolean(priorityScheme.description),
				prioritySchemeProjects: priorityScheme.projects.values.map(({ id }) => id),
				prioritySchemeProjectsCount: priorityScheme.projects.total,
			};

	return {
		attributes,
		sourceName: SCREEN_ANALYTICS_EVENT_SOURCE_NAME,
	};
};

const generateProjectsMetaForTrackEvent = (
	existingProjects: Project[],
	existingProjectsCount: number,
	projectsDiff: Diff,
) => {
	const existingProjectIds = existingProjects.map(({ id }) => id);
	let prioritySchemeProjects = existingProjectIds;
	let prioritySchemeProjectsCount = existingProjectsCount;

	const { add, remove } = projectsDiff;

	if (remove.length > 0) {
		prioritySchemeProjects = prioritySchemeProjects.filter((id) => !remove.includes(id));
		prioritySchemeProjectsCount -= remove.length;
	}

	if (add.length > 0) {
		prioritySchemeProjects = [...prioritySchemeProjects, ...add];
		prioritySchemeProjectsCount += add.length;
	}

	return {
		prioritySchemeProjects,
		prioritySchemeProjectsCount,
	};
};

const generateTrackEventMeta = (
	initialPriorityScheme: CreateOrEditPriorityScheme,
	priorityScheme: CreateOrEditPriorityScheme,
	createdPrioritySchemeId: string,
	projectsDiff: Diff,
) => {
	const actionSubjectAndAction: ActionSubjectAndAction = !priorityScheme.id
		? `${addPrioritySchemeTrackEvent.actionSubject} ${addPrioritySchemeTrackEvent.action}`
		: `${editPrioritySchemeTrackEvent.actionSubject} ${editPrioritySchemeTrackEvent.action}`;

	const actionSubjectId = !priorityScheme.id
		? addPrioritySchemeTrackEvent.actionSubjectId
		: editPrioritySchemeTrackEvent.actionSubjectId;

	const attributes = {
		prioritySchemeId: priorityScheme.id ?? createdPrioritySchemeId,
		priorities: priorityScheme.priorities.map(({ id }) => id),
		prioritySchemePriorityCount: priorityScheme.priorities.length,
		prioritySchemeHasDescription: Boolean(priorityScheme.description),
		...(!priorityScheme.id
			? {
					prioritySchemeProjects: projectsDiff.add,
					prioritySchemeProjectsCount: projectsDiff.add.length,
				}
			: generateProjectsMetaForTrackEvent(
					initialPriorityScheme.projects.values,
					initialPriorityScheme.projects.total,
					projectsDiff,
				)),
	};

	return {
		actionSubjectAndAction,
		actionSubjectId,
		attributes,
	};
};

export const fireSaveAnalyticsEvent = (
	createAnalyticsEvent: (_payload: AnalyticsAttributes) => UIAnalyticsEvent,
	initialScheme: CreateOrEditPriorityScheme,
	priorityScheme: CreateOrEditPriorityScheme,
	createdPrioritySchemeId: string,
	projectsDiff: Diff,
) => {
	const { actionSubjectAndAction, actionSubjectId, attributes } = generateTrackEventMeta(
		initialScheme,
		priorityScheme,
		createdPrioritySchemeId,
		projectsDiff,
	);

	fireTrackAnalytics(createAnalyticsEvent({}), actionSubjectAndAction, actionSubjectId, attributes);
};

export const mapPriorityMappingsToRequestMappings = (
	inMappings: PriorityMappings,
	outMappings: PriorityMappings,
): RequestMappings => {
	const mapToRequestMappingArray = (mappings: PriorityMappings) => {
		const results: Record<number, number> = {};
		Object.entries(mappings).forEach(([fromPriorityId, toPriority]) => {
			results[Number(fromPriorityId)] = Number(toPriority.id);
		});
		return results;
	};

	return {
		in: mapToRequestMappingArray(inMappings),
		out: mapToRequestMappingArray(outMappings),
	};
};
